import { countBy } from 'lodash';
import type {
  Scalars,
  SharesVisibilityForProjectFragment,
} from '@frameio/graphql-client/generated';
import {
  useBulkToggleShareVisibilityMutation,
  useSharesVisibilityForProjectFragment,
} from '@frameio/graphql-client/generated';
import { getIsShareExpired } from '@frameio/shared/shares/utils';
import { EMPTY_ARRAY } from '@frameio/utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import TEXT from './generated/TEXT/useBulkToggleShareVisibility';

TEXT.DEFINE({
  /** Error message shown when bulk visibility toggle fails */
  errorMessage: "Something went wrong when updating the Share's visibility",
});
type Props = {
  projectId: Scalars['ID']['output'];
  shareIds: Array<Scalars['ID']['output']>;
};

function countShareVisibility(
  shares: SharesVisibilityForProjectFragment['shares']['nodes']
) {
  const sharesCorrectedForExpiration = shares?.map(
    ({ enabled, expiration, expiresIn, ...other }) => {
      const isExpired = getIsShareExpired({ expiration, expiresIn });
      return {
        ...other,
        expiration,
        expiresIn,
        enabled: isExpired ? false : enabled,
      };
    }
  );

  const { true: enabledCount = 0, false: disabledCount = 0 } = countBy(
    sharesCorrectedForExpiration,
    'enabled'
  );
  return { enabledCount, disabledCount };
}

/**
 * @param {string[]} shareIds - An array of `Share.id`s
 * @param {string} projectId - The `id` of the project currently being viewed
 * @returns {Object}
 * @property {VisibilityAction} actionType - The relevant action to be taken
 *  based on the state of the `enabled` property for the majority of the
 *  `Share`s referenced in `shareIds`.
 * @property {Function} bulkToggleVisibility - A handler for calling the
 *  `BulkToggleShareVisibilityMutation` operation on `shareIds`.
 */
export function useBulkToggleShareVisibility({
  shareIds = EMPTY_ARRAY,
  projectId,
}: Props) {
  const { data } = useSharesVisibilityForProjectFragment({
    variables: { filterBy: { name: undefined } },
    from: { __typename: 'Project', id: projectId },
  });
  const { enabledCount, disabledCount } = countShareVisibility(
    (data as SharesVisibilityForProjectFragment)?.shares?.nodes.filter(
      ({ id }) => shareIds?.includes(id)
    )
  );

  const actionType = enabledCount >= disabledCount ? 'disable' : 'enable';

  const [toggleSharesVisibility] = useBulkToggleShareVisibilityMutation({
    variables: {
      input: {
        shareIds,
        enabled: actionType === 'enable',
        expiration: actionType === 'enable' ? null : undefined,
      },
    },
  });

  const tryCatch = useToastTryCatchHandler();

  return {
    actionType: actionType as 'enable' | 'disable',
    bulkToggleVisibility: () =>
      tryCatch({
        attempt: toggleSharesVisibility,
        errorMessage: TEXT.errorMessage,
      }),
  };
}
